import React, { useMemo } from 'react';
import DocumentTemplateTextfield from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateTextfield';
import DocumentTemplateTextAreas from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateTextAreas';
import DocumentTemplateSignature from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateSignature';
import DocumentTemplateCheckbox from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateCheckbox';
import DocumentTemplateSmart from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateSmart';
import DocumentTemplatePhysicianStamp from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplatePhysicianStamp';
import DocumentTemplatePatientStamp from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplatePatientStamp';
import DocumentTemplateUtils from '../../../../../services/DocumentTemplateUtils';

const DocumentTemplateFields = (props) => {
  const { updateField } = props;

  function handleStop(id, x, y) {
    updateField(id, { x, y });
  }

  return (
    <>
      <Smart {...props} handleStop={handleStop} />
      <TextFields {...props} handleStop={handleStop} />
      <TextAreas {...props} handleStop={handleStop} />
      <Signatures {...props} handleStop={handleStop} />
      <Svgs {...props} />
      <Boxes {...props} handleStop={handleStop} />
      <PhysicianStamps {...props} handleStop={handleStop} />
      <PatientStamps {...props} handleStop={handleStop} />
      <InputDescriptions {...props} />
      <AiProvenanceOverlay {...props} />
    </>
  );
};

const AI_SOURCE_COLORS = { ffdnet: '#dc2828', gemini: '#9628c8' };
const AI_LOW_CONFIDENCE = 0.4;

// Review overlay for AI-detected fields (edit mode only): colored outline per
// detection source, dashed when low-confidence, and the bound placeholder shown
// as a badge so a wrong binding is visible without opening each field.
// Markers disappear once the admin uses "Mark Reviewed" in the edit panel
// (which strips source/confidence from the inputs).
function AiProvenanceOverlay(props) {
  const { pageWidth, pageHeight, editMode, inputs } = props;
  const fields = useMemo(() => {
    return (inputs || [])
      .filter((it) => !!it.source)
      .map((field) => {
        const x = DocumentTemplateUtils.percentToPixel(field.x_percent, pageWidth);
        const y = DocumentTemplateUtils.percentToPixel(field.y_percent, pageHeight);
        const w = field.w_percent
          ? DocumentTemplateUtils.percentToPixel(field.w_percent, pageWidth)
          : 18;
        const h = field.h_percent
          ? DocumentTemplateUtils.percentToPixel(field.h_percent, pageHeight)
          : 18;
        const lowConfidence =
          field.confidence !== undefined && field.confidence < AI_LOW_CONFIDENCE;
        const color = AI_SOURCE_COLORS[field.source] || '#888';
        return (
          <React.Fragment key={`ai-${field.id}`}>
            <div
              style={{
                position: 'absolute',
                top: `${y}px`,
                left: `${x}px`,
                width: `${w}px`,
                height: `${h}px`,
                border: `2px ${lowConfidence ? 'dashed' : 'solid'} ${color}`,
                borderRadius: '2px',
                pointerEvents: 'none',
                zIndex: 98,
                opacity: 0.85,
              }}
            />
            {!!field.placeholder && field.placeholder !== 'empty' && (
              <div
                style={{
                  position: 'absolute',
                  top: `${y + h}px`,
                  left: `${x}px`,
                  background: color,
                  color: 'white',
                  fontSize: '9px',
                  padding: '0 3px',
                  borderRadius: '0 0 3px 3px',
                  pointerEvents: 'none',
                  zIndex: 98,
                  whiteSpace: 'nowrap',
                }}
              >
                {DocumentTemplateUtils.getPlaceholderLabel(field.placeholder)}
              </div>
            )}
          </React.Fragment>
        );
      });
  }, [inputs, pageWidth, pageHeight]);

  if (!editMode || fields.length === 0) return null;
  return <div className="input-wrapper ai-provenance-overlay">{fields}</div>;
}

function InputDescriptions(props) {
  const { pageWidth, pageHeight } = props;
  const fields = useMemo(() => {
    return props.inputs
      .filter((it) => !!it.description)
      .map((field) => {
        const position = {
          x: DocumentTemplateUtils.percentToPixel(field.x_percent, pageWidth),
          y: DocumentTemplateUtils.percentToPixel(field.y_percent, pageHeight),
          w: DocumentTemplateUtils.percentToPixel(field.w_percent, pageWidth),
          h: DocumentTemplateUtils.percentToPixel(field.h_percent, pageHeight),
        };
        const style = {
          top: `${position.y}px`,
          left: `${position.x}px`,
          position: 'absolute',
          background: '#2693d1',
          padding: '1px 2px',
          borderRadius: '4px',
          color: 'white',
          fontSize: '10px',
          maxWidth: `${position.w}px`,
          maxHeight: '1.5em',
          overflow: 'hidden',
          zIndex: 99,
        };
        return (
          <div className="input-description" style={style}>
            {field.description}
          </div>
        );
      });
  }, [pageHeight, pageWidth, props.inputs]);

  if (!props.showInputDescriptions || !props.editMode) {
    return null;
  }

  return <div className="input-wrapper">{fields}</div>;
}

const TextFields = (props) => {
  let {
    inputs,
    pageWidth,
    pageHeight,
    fieldValues,
    editMode,
    prefill,
    selectedEditElement,
    handleStop,
    updateField,
    selectEditElement,
    onChange,
    toggleQuickPlaceholder,
    role,
    macroData,
  } = props;

  return inputs
    .filter((input) => input.type === 'textfield')
    .map((field) => (
      <DocumentTemplateTextfield
        key={field.id}
        field={field}
        selectedEditElement={selectedEditElement}
        editMode={editMode}
        prefill={prefill}
        fieldValues={fieldValues}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        selectEditElement={selectEditElement}
        handleStop={handleStop}
        onChange={onChange}
        updateField={updateField}
        toggleQuickPlaceholder={toggleQuickPlaceholder}
        role={role}
        macroData={macroData}
      />
    ));
};

const TextAreas = (props) => {
  let {
    inputs,
    pageWidth,
    pageHeight,
    fieldValues,
    editMode,
    prefill,
    selectedEditElement,
    handleStop,
    updateField,
    selectEditElement,
    onChange,
    toggleQuickPlaceholder,
    role,
    macroData,
  } = props;

  return inputs
    .filter((input) => input.type === 'textarea')
    .map((field) => (
      <DocumentTemplateTextAreas
        key={field.id}
        field={field}
        selectedEditElement={selectedEditElement}
        editMode={editMode}
        prefill={prefill}
        fieldValues={fieldValues}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        selectEditElement={selectEditElement}
        handleStop={handleStop}
        onChange={onChange}
        updateField={updateField}
        toggleQuickPlaceholder={toggleQuickPlaceholder}
        role={role}
        macroData={macroData}
      />
    ));
};

const Signatures = (props) => {
  let {
    signatureUrl,
    inputs,
    pageWidth,
    pageHeight,
    editMode,
    handleStop,
    updateField,
    selectEditElement,
    onChange,
    patientSignature,
    setPatientSignature,
    role,
  } = props;

  return inputs
    .filter((input) => input.type === 'signature' || input.type === 'patient signature')
    .map((signature) => {
      const isPatient = signature.type === 'patient signature';
      return (
        (isPatient || signatureUrl !== undefined || editMode) && (
          <React.Fragment key={signature.id}>
            <DocumentTemplateSignature
              field={signature}
              signatureUrl={isPatient ? patientSignature : signatureUrl}
              selectEditElement={selectEditElement}
              editMode={editMode}
              pageWidth={pageWidth}
              pageHeight={pageHeight}
              handleStop={handleStop}
              updateField={updateField}
              onChange={onChange}
              isPatient={signature.type === 'patient signature'}
              setPatientSignature={setPatientSignature}
              role={role}
            />
          </React.Fragment>
        )
      );
    });
};

const PhysicianStamps = (props) => {
  let {
    inputs,
    pageWidth,
    pageHeight,
    editMode,
    handleStop,
    updateField,
    selectEditElement,
    selectedEditElement,
    onChange,
    prefill,
  } = props;

  return inputs
    .filter((input) => input.type === 'stamp')
    .map((stamp) => {
      return (
        <DocumentTemplatePhysicianStamp
          field={stamp}
          selectEditElement={selectEditElement}
          editMode={editMode}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
          handleStop={handleStop}
          updateField={updateField}
          onChange={onChange}
          selectedEditElement={selectedEditElement}
          prefill={prefill}
        />
      );
    });
};

const PatientStamps = (props) => {
  let {
    inputs,
    pageWidth,
    pageHeight,
    editMode,
    handleStop,
    updateField,
    selectEditElement,
    selectedEditElement,
    onChange,
    prefill,
  } = props;

  return inputs
    .filter((input) => input.type === 'patient stamp')
    .map((stamp) => {
      return (
        <DocumentTemplatePatientStamp
          field={stamp}
          selectEditElement={selectEditElement}
          editMode={editMode}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
          handleStop={handleStop}
          updateField={updateField}
          onChange={onChange}
          selectedEditElement={selectedEditElement}
          prefill={prefill}
        />
      );
    });
};

const Svgs = (props) => {
  const { index, fieldValues } = props;

  let inputs = (fieldValues?.svg || {})[index] || [];

  return inputs
    .filter((input) => input.type === 'svg')
    .map((field) => (
      <span
        key={`svg-${field.id}`}
        className="template-field svg-element"
        data-field-id={field.id}
        dangerouslySetInnerHTML={{ __html: DocumentTemplateUtils.sanitizeSvg(field.svg) }}
        style={{
          position: 'absolute',
          height: '100%',
          width: '100%',
          zIndex: '5',
          pointerEvents: 'none',
        }}
      />
    ));
};

const Boxes = (props) => {
  let {
    inputs,
    pageWidth,
    pageHeight,
    fieldValues,
    prefill,
    editMode,
    selectedEditElement,
    handleStop,
    selectEditElement,
    onChange,
    updateField,
    toggleQuickPlaceholder,
    role,
  } = props;
  return inputs
    .filter((input) => input.type === 'checkbox')
    .map((field, index) => (
      <DocumentTemplateCheckbox
        key={field.id}
        field={field}
        selectedEditElement={selectedEditElement}
        editMode={editMode}
        prefill={prefill}
        fieldValues={fieldValues}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        selectEditElement={selectEditElement}
        handleStop={handleStop}
        onChange={onChange}
        updateField={updateField}
        toggleQuickPlaceholder={toggleQuickPlaceholder}
        role={role}
      />
    ));
};

const Smart = (props) => {
  let {
    inputs,
    pageWidth,
    pageHeight,
    fieldValues,
    editMode,
    prefill,
    selectedEditElement,
    handleStop,
    updateField,
    selectEditElement,
    onChange,
    change,
    toggleQuickPlaceholder,
    macroData,
  } = props;

  return inputs
    .filter((input) => input.type === 'smart')
    .map((field) => (
      <DocumentTemplateSmart
        key={field.id}
        field={field}
        selectedEditElement={selectedEditElement}
        editMode={editMode}
        prefill={prefill}
        fieldValues={fieldValues}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        selectEditElement={selectEditElement}
        handleStop={handleStop}
        change={change}
        onChange={onChange}
        updateField={updateField}
        inputs={inputs}
        toggleQuickPlaceholder={toggleQuickPlaceholder}
        macroData={macroData}
      />
    ));
};

export default DocumentTemplateFields;
